import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Flex,
  Link,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ReclaimProofRequest } from '@reclaimprotocol/js-sdk';
import { useAppSelector } from '../../../../redux/hooks';
import { getParamsAdded } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import { getManualVerifcationProviderVerificationUrl } from '../../../../utils/manual-verification-session/utils';
import { makeProviderActive } from '../../../../utils/provider-builder/register-provider-utils';
import { getFirebaseIdToken } from '../../../../utils/utils';

interface VerifyProviderProps {
  httpProviderId: string;
  setProviderVerified: (value: boolean) => void;
}

export const VerifyProvider = (props: VerifyProviderProps) => {
  const toast = useToast();

  const { httpProviderId, setProviderVerified } = props;
  const providerId = httpProviderId;

  const [url, setUrl] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const paramsAdded = useAppSelector(getParamsAdded);

  useEffect(() => {
    if (!url) {
      generateVerificationRequest();
    }
  }, [url]);

  async function updateProviderOnSuccessfulProof() {
    try {
      const accessToken = (await getFirebaseIdToken()) ?? '';
      const message = await makeProviderActive(accessToken, providerId);
      if (!message.isSuccess) {
        toast({
          title: 'Error while updating provider',
          description: message.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      } else {
        toast({
          title: 'Provider Verified Successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setProviderVerified(true);
        return;
      }
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error while updating provider',
        description: 'Unknown Error while updating provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function generateVerificationRequest() {
    try {
      setButtonLoading(true);
      const response = await getManualVerifcationProviderVerificationUrl(
        providerId,
        paramsAdded
      );
      if (response?.message === 'successful') {
        const reclaimProofRequest = await ReclaimProofRequest.fromJsonString(
          response?.reclaimProofRequestConfig
        );
        const requestUrl = await reclaimProofRequest.getRequestUrl();
        setUrl(requestUrl);

        await reclaimProofRequest.startSession({
          onSuccess: (proofs) => {
            if (proofs) {
              console.log('Proof received:', proofs);
              setIsVerified(true);
              updateProviderOnSuccessfulProof();
            }
          },
          onError: (error) => {
            console.error('Verification failed', error);
            toast({
              title: 'Provider verification failed',
              description: 'Please edit provider and try again',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          },
        });
      } else {
        throw Error('Could not generate verification link');
      }
    } catch (error) {
      toast({
        title: 'Provider verification failed',
        description: 'Could not generate verification link',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('Verification failed', error);
    } finally {
      setButtonLoading(false);
    }
  }

  return (
    <Box minH="300px" h="100%" gap={4}>
      <Center>
        {url ? (
          <>
            <VStack>
              <QRCode size={230} value={`${url}`} />

              <Text mt="20px">Or Visit</Text>

              <Link
                href={url}
                isExternal
                color="blue.500"
                _hover={{ textDecoration: 'underline' }}
              >
                {url}
              </Link>
            </VStack>
          </>
        ) : (
          <Flex width="230px" height="230px" alignItems="center" justifyContent="center">
            <CircularProgress color="#000" isIndeterminate size="50px" />
          </Flex>
        )}
      </Center>
      <Center>
        <Button
          bgColor="#000"
          color="#fff"
          colorScheme="blackAlpha"
          mt="30px"
          onClick={generateVerificationRequest}
          _hover={{
            fontWeight: 'bold',
            transform: 'scale(1.05)',
          }}
          isLoading={buttonLoading}
        >
          Create New Verification Link
        </Button>
      </Center>
    </Box>
  );
};
